import { forwardRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * PasswordInput — text input with show/hide toggle (eye icon).
 *
 * Drop-in replacement for <input type="password" />. Accepts all the usual
 * props (value, onChange, className, autoComplete, minLength, required, etc.)
 * via spread. The eye toggles native input.type between "password" and "text".
 *
 * data-testid is forwarded to the underlying <input> so existing test hooks
 * keep working.
 */
const PasswordInput = forwardRef(function PasswordInput(
    { className = "bm-input", style, ...rest },
    ref,
) {
    const [visible, setVisible] = useState(false);
    return (
        <div style={{ position: "relative", ...style }}>
            <input
                ref={ref}
                {...rest}
                type={visible ? "text" : "password"}
                className={className}
                style={{ paddingRight: 40 }}
            />
            <button
                type="button"
                tabIndex={-1}
                aria-label={visible ? "Hide password" : "Show password"}
                onClick={() => setVisible((v) => !v)}
                data-testid={`${rest["data-testid"] || "password"}-toggle`}
                style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: 4,
                    color: "var(--bm-text-muted)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {visible ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
        </div>
    );
});

export default PasswordInput;
